import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useAuth, logEvent } from "@um/ui";
import type { Item, WithId } from "@um/contracts";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useItemsStore } from "@/store/useItemsStore";
import { useCompanion } from "@/quests/useCompanion";
import { useRewardMind } from "@/quests/useRewardMind";
import { itemImageUrl, itemsForMind } from "./itemState";
import { ItemArt } from "./ItemShowcase";

/** Announces an item granted in answer to a survey wish. */
export function WishRevealHost() {
  const { accountId } = useAuth();
  const { pathname } = useLocation();
  // Both checks before the body's hooks, so neither can change the hook count
  // on the render where a wish lands or the user navigates away.
  if (!accountId || pathname !== "/") return null;
  return <WishRevealHostBody key={accountId} accountId={accountId} />;
}

function WishRevealHostBody({ accountId }: { accountId: string }) {
  const navigate = useNavigate();
  const mind = useRewardMind();
  const companion = useCompanion();
  const items = useItemsStore((s) => s.items);
  const markWishRevealed = useItemsStore((s) => s.markWishRevealed);
  const subscribeToItems = useItemsStore((s) => s.subscribeToItems);
  const unsubscribeFromItems = useItemsStore((s) => s.unsubscribeFromItems);
  /** Dismissed this session, so closing does not immediately re-open. */
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Items are reference-counted, so holding a subscription app-wide costs one
  // listener and cannot tear down My Stuff's.
  useEffect(() => {
    subscribeToItems(accountId);
    return () => {
      unsubscribeFromItems();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const granted: WithId<Item> | undefined = useMemo(() => {
    const now = Date.now();
    return (
      itemsForMind(items, mind?.ref.id)
        .filter(
          (item) =>
            !!item.wish &&
            // Shown once. A dismissed reveal leaves the item in My Stuff as an
            // ordinary parcel rather than announcing itself again on every launch.
            !item.wishRevealedAt &&
            !item.openedAt &&
            !item.generationFailedAt &&
            // Both are required: the art is the whole reveal, and an item still in
            // transit has not arrived yet. Announcing either early shows an empty
            // frame or spoils a parcel that has not landed.
            !!itemImageUrl(item) &&
            !!item.deliverAt &&
            item.deliverAt.toMillis() <= now &&
            !dismissed.includes(item.id)
        )
        // Oldest first — a wish that has waited longest is answered first.
        .sort(
          (a, b) =>
            (a.created?.toMillis?.() ?? 0) - (b.created?.toMillis?.() ?? 0)
        )[0]
    );
  }, [items, mind?.ref.id, dismissed]);

  useEffect(() => {
    if (granted) {
      logEvent("um_wish_revealed", {
        item_id: granted.id,
        item_name: granted.name,
        slot: granted.slot,
      });
    }
    // Once per item, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granted?.id]);

  if (!granted) return null;

  /** Equipping opens the item; dismissing only records that the reveal was seen. */
  const close = async (equip: boolean) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await markWishRevealed(accountId, granted.id, equip);
      if (!mounted.current) return;
      setDismissed((seen) => [...seen, granted.id]);
      if (!equip) return;
      logEvent("um_wish_equip_tapped", { item_id: granted.id });
      const characterId = companion.character?.ref.id;
      navigate(
        characterId ? `/characters/${characterId}/card?wardrobe=1&item=${encodeURIComponent(granted.id)}&from=wish` : "/characters",
        {
          state: { wardrobe: true, mindId: granted.mind?.id },
        }
      );
    } catch {
      toast.error("Couldn't save your item. Please try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && void close(false)}>
      <DialogContent className="max-h-[85dvh] max-w-sm overflow-y-auto text-center">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-brand-teal" aria-hidden />
            Thanks for your feedback
          </DialogTitle>
          <DialogDescription className="text-base font-medium text-foreground">
            Your wish came true
          </DialogDescription>
        </DialogHeader>

        <div className="my-2">
          <ItemArt item={granted} className="mx-auto w-full max-w-[15rem]" />
          {granted.name && (
            <p className="mt-3 text-lg font-semibold">{granted.name}</p>
          )}
          {/* Their own words, kept but demoted below the item. It is what makes
              this read as an answer rather than another parcel, and it is the
              only thing here that could not have been written in advance. */}
          <p className="mt-1 text-sm text-gray-500">
            You asked for “{granted.wish}”
          </p>
        </div>

        <Button
          className="w-full"
          disabled={saving}
          onClick={() => void close(true)}
        >
          {saving ? "Saving…" : `Try it ${granted.slot === "accessory" ? "with" : "on"} ${companion.name}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
